import React, { FC, useEffect } from 'react'
import {
  Box,
  Flex,
  Image,
  TertiaryButton,
  Text,
} from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { theme } from '@moonpig/launchpad-theme'
import { Occasion } from '@moonpig/web-explore-types-graphql'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { IconSystemChevronLeft } from '@moonpig/launchpad-assets'
import { PromptReminder } from './types'
import { buildReminderBannerTitle, buildReminderSubtitleDays } from './utils'
import {
  ReminderBannerImageBackgroundColor,
  ReminderBannerImage,
  ReminderBannerBackground,
} from './brand-config'
import { useFindLocaleText } from '../../text-localisation'
import { getLabelFromOccasion } from './ocassionMap'

type BannerImageProps = {
  occasion: Occasion
}

type ReminderBannerProps = {
  reminder: PromptReminder
  title: string
  onClose: () => void
}

const DESKTOP_IMAGE_SIZE = theme.spacing[11]

const StyledBanner = styled(Flex)`
  background: ${ReminderBannerBackground};
  position: relative;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  ${s({
    padding: { xs: 6, md: 8 },
    marginTop: -4,
    gap: { md: 16 },
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'flex-start', md: 'center' },
  })};
`

const ImageContainer = styled(Box)`
  background: ${ReminderBannerImageBackgroundColor};
  ${s({
    padding: 6,
    borderRadius: 8,
    width: '100px',
    height: '100px',
    boxShadow: theme.shadows.boxShadow01,
    position: 'relative',
  })};
`

const MobileBannerImage = styled(Image)`
  align-self: flex-end;
  position: absolute;
  bottom: 0;
  right: 0;
  ${s({
    display: { xs: 'block', md: 'none' },
  })}
`

const DesktopBannerImage = styled(Image)`
  width: ${DESKTOP_IMAGE_SIZE};
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: block;
`

const BannerWrapper = styled(Flex)`
  min-height: ${theme.spacing[13]}px;
  ${s({
    margin: { md: 'auto' },
  })};
  flex: 1;
  max-width: 1200px;
`

const BannerContent = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
`

const StyledGoBackButton = styled(TertiaryButton)`
  ${s({
    padding: 0,
    marginBottom: 4,
    minHeight: 'auto',
    justifyContent: 'flex-start',
  })};
`

const DesktopBannerImageContainer: FC<BannerImageProps> = ({ occasion }) => {
  const imageSrc = ReminderBannerImage(occasion, false)

  return (
    <ImageContainer display={{ xs: 'none', md: 'block' }}>
      <DesktopBannerImage
        src={imageSrc}
        alt={`${occasion.toLowerCase().replace(/_/g, ' ')} icon`}
      />
    </ImageContainer>
  )
}

const MobileBannerImageContainer: FC<BannerImageProps> = ({ occasion }) => {
  const imageSrc = ReminderBannerImage(occasion, true)
  return (
    <MobileBannerImage
      src={imageSrc}
      alt={`${occasion.toLowerCase().replace(/_/g, ' ')} icon`}
    />
  )
}

export const ReminderBanner: FC<ReminderBannerProps> = ({
  reminder,
  title,
  onClose,
}) => {
  useEffect(() => {
    trackGAEvent({
      event: 'impression',
      event_data: {
        category: 'reminders',
        action: 'view banner',
        label: `reminder banner | ${reminder.occasion.toLowerCase()}`,
      },
    })
  }, [reminder.occasion])

  const localiseText = useFindLocaleText()
  const occasionLabel = getLabelFromOccasion(reminder.occasion)
  const reminderTimeSubtitle = buildReminderSubtitleDays({
    reminder,
    localiseText,
  })

  const handleClose = () => {
    onClose()

    trackGAEvent({
      event: 'select_content',
      event_data: {
        category: 'reminders',
        action: 'close selected',
        label: `reminder banner | ${reminder.occasion.toLowerCase()}`,
      },
    })
  }

  return (
    <StyledBanner>
      <BannerWrapper>
        <BannerContent>
          <StyledGoBackButton
            leadingIcon={<IconSystemChevronLeft />}
            onClick={handleClose}
          >
            {localiseText('find.all', { label: title })}
          </StyledGoBackButton>

          <Text
            typography={{
              xs: 'typeMobileDisplay03',
              lg: 'typeDesktopDisplay03',
            }}
            width={{ xs: '95%', md: '100%' }}
            marginBottom={{ xs: 4, md: 0 }}
            color={theme.colors.colorTextHeadline}
          >
            {buildReminderBannerTitle({
              localiseText,
              title,
              reminderName: reminder.name,
            })}
          </Text>
          <Box>
            <Text
              typography={'typeBodyText'}
              color={theme.colors.colorTextBody}
              marginBottom={8}
            >
              {`${occasionLabel} in `}
            </Text>
            <Text typography="typeBodyLabel" color={theme.colors.colorTextBody}>
              {reminderTimeSubtitle}
            </Text>
          </Box>
        </BannerContent>
        <MobileBannerImageContainer occasion={reminder.occasion} />
        <DesktopBannerImageContainer occasion={reminder.occasion} />
      </BannerWrapper>
    </StyledBanner>
  )
}
